import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { cn } from '@/lib/utils.js';
import { toast } from '@/components/ui/toaster.jsx';
import { updateNamedInsured, resolveAccount, searchAccountContacts, setPrimaryContact, suggestAddresses } from '@/services/account.service.js';
import { ApiError } from '@/lib/api.js';
import { usePeekStore } from '@/stores/peek-store.js';
import { openContactPeek } from '@/lib/links.js';
import { HANGAR_SUBMISSION_UPDATED_EVENT } from '@/components/account/SubmissionPanel.jsx';
import { addressForSave, PARTIAL_ADDRESS_WORDS } from '@/lib/named-insured-address.js';

// Edit the insured name on the Account (§2.2). Writes Accounts.Account_Name —
// the canonical insured display used across the pipeline, rail, headers,
// proposals, and bundles — so the change re-titles EVERY term under this
// account (the warning in the sheet says so).
//
// Policyholder_Type is the live Zoho picklist. Every value except Individual is
// already an entity, so actual_value "Entity" — which specifically means an LLC
// — just displays "LLC" (a corporation → Corporation, etc.). There is NO "Joint"
// value: when a co-owner is added the type moves off Individual — default
// "Partnership" (both owners named in Account_Name) or "LLC". Labels here track
// the stored actual_value.

export const POLICYHOLDER_TYPES = [
  // Reads as a prompt, not a valid resting state — it is no longer savable.
  { value: '', label: '— Select type —' },
  { value: 'Individual', label: 'Individual' },
  { value: 'Entity', label: 'LLC' },
  { value: 'Corporation', label: 'Corporation' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'Non-Profit Organization', label: 'Non-Profit Organization' },
];

const fieldClass = cn(
  'flex w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
  'placeholder:text-text-dim',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

export default function EditNamedInsuredSheet({
  accountId,
  initialName = '',
  initialPolicyholderType = '',
  // Hangar/property deals only: the server also writes the new name into the
  // session's application overlay and regenerates the packet PDF, so the
  // rename reaches the submission document, not just the CRM display
  // (2026-07-07: a rename here only changed the email narrative).
  cascadeDealId = null,
  open,
  onOpenChange,
  onSaved,
}) {
  const [name, setName] = useState(initialName);
  const openPeek = usePeekStore((s) => s.openPeek);
  const [policyholderType, setPolicyholderType] = useState(initialPolicyholderType);
  const [submitting, setSubmitting] = useState(false);
  // Authoritative current values fetched from the Account on open — the baseline
  // for change-detection AND the seed when no override is supplied. The pipeline
  // item does NOT carry policyholder_type, so without this the sheet reseeds
  // blank and a saved entity type looks unsaved (bug 2026-06-19).
  const [loadedName, setLoadedName] = useState(initialName);
  const [loadedType, setLoadedType] = useState('');
  // Entity address + primary-contact phone/email (item D). Seeded from the
  // account resolve on open; edited in place. loadedContact is the change-
  // detection baseline so only touched fields ride the sparse PATCH.
  const EMPTY_CONTACT = { street: '', street2: '', city: '', st: '', zip: '', phone: '', email: '' };
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [loadedContact, setLoadedContact] = useState(EMPTY_CONTACT);
  const setContactField = (k, v) => setContact((c) => ({ ...c, [k]: v }));
  // WHO the primary contact is (CDCR class: the signer must be picked from
  // people who exist, or created parented to this account). Selecting acts
  // immediately — the server writes Accounts.Primary_Contact and cascades
  // Deal.Contact_Name through the one existing writer.
  const [primary, setPrimary] = useState({ id: null, name: '' });
  const [pickerQ, setPickerQ] = useState('');
  const [pickerResults, setPickerResults] = useState(null);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerFailed, setPickerFailed] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const EMPTY_NEW = { first_name: '', last_name: '', email: '', phone: '' };
  const [newContact, setNewContact] = useState(EMPTY_NEW);
  const [settingPrimary, setSettingPrimary] = useState(false);
  // Radar address suggestions. Free text always keeps working — picking a
  // suggestion just fills the four fields SEPARATELY; nothing is required to
  // come from the dropdown, and with no key configured it simply never shows.
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);

  // `initialPolicyholderType` is an OVERRIDE/suggestion (the co-owner chain
  // pre-selects 'Partnership'); when falsy the sheet shows the account's true
  // current type fetched here. Account_Name is also refreshed to the live value.
  useEffect(() => {
    if (!open) return;
    setName(initialName || '');
    setPolicyholderType(initialPolicyholderType || '');
    setLoadedName(initialName || '');
    setLoadedType('');
    setContact(EMPTY_CONTACT);
    setLoadedContact(EMPTY_CONTACT);
    if (!accountId) return;
    let alive = true;
    resolveAccount(accountId)
      .then((acct) => {
        if (!alive || !acct) return;
        const curName = acct.account_name || '';
        const curType = acct.policyholder_type || '';
        setLoadedName(curName);
        setLoadedType(curType);
        // Only adopt the fetched name/type if the broker hasn't started editing.
        setName((n) => (n === (initialName || '') ? curName : n));
        if (!initialPolicyholderType) {
          setPolicyholderType((t) => (t === '' ? curType : t));
        }
        const curContact = {
          street: acct.account_street || '',
          street2: acct.account_street_2 || '',
          city: acct.account_city || '',
          st: acct.account_st || '',
          zip: acct.account_zip || '',
          phone: acct.primary_contact_phone || '',
          email: acct.primary_contact_email || '',
        };
        setLoadedContact(curContact);
        setPrimary({ id: acct.primary_contact_id || null, name: acct.primary_contact_name || '' });
        // Adopt fetched values only for fields the broker hasn't touched (still
        // blank from the reset above), so a slow fetch can't clobber an edit.
        setContact((c) => {
          const next = { ...c };
          for (const k of Object.keys(curContact)) if (next[k] === '') next[k] = curContact[k];
          return next;
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [open, accountId, initialName, initialPolicyholderType]);

  // Debounced typeahead. Distinguishes "not searched" (null) from "searched,
  // nothing found" ([]) so the empty state never shows before a search ran, and
  // a FAILED search (pickerFailed: at account_people "both" the picker answers
  // 503 could_not_check) from an empty one: it says it could not search.
  useEffect(() => {
    const q = pickerQ.trim();
    setPickerFailed(false);
    if (q.length < 2) { setPickerResults(null); return undefined; }
    let alive = true;
    setPickerBusy(true);
    const t = setTimeout(() => {
      searchAccountContacts(accountId, q)
        .then((r) => { if (alive) setPickerResults(r?.contacts || []); })
        .catch(() => { if (alive) { setPickerResults([]); setPickerFailed(true); } })
        .finally(() => { if (alive) setPickerBusy(false); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [pickerQ, accountId]);

  // Debounced street lookup; a failed or empty lookup closes the dropdown and
  // costs nothing.
  useEffect(() => {
    const q = contact.street.trim();
    if (q.length < 4 || !addrOpen) { setAddrSuggestions([]); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      suggestAddresses(q)
        .then((r) => { if (alive) setAddrSuggestions(r?.suggestions || []); })
        .catch(() => { if (alive) setAddrSuggestions([]); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [contact.street, addrOpen]);

  function pickAddress(s) {
    setContact((c) => ({ ...c, street: s.street, city: s.city, st: s.state, zip: s.zip }));
    setAddrOpen(false);
    setAddrSuggestions([]);
  }

  async function applyPrimary(body, label) {
    setSettingPrimary(true);
    try {
      const r = await setPrimaryContact(accountId, body);
      setPrimary({ id: r?.contact?.id || null, name: r?.contact?.name || label || '' });
      setPickerQ(''); setPickerResults(null); setShowCreate(false); setNewContact(EMPTY_NEW);
      toast.success(`Primary contact set: ${r?.contact?.name || label}`);
      for (const w of r?.warnings || []) toast.info(w);
      if (typeof onSaved === 'function') onSaved({ primary_contact_id: r?.contact?.id });
    } catch (err) {
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message || 'failed';
      toast.error(`Couldn't set primary contact: ${msg}`);
    } finally {
      setSettingPrimary(false);
    }
  }

  const trimmed = name.trim();
  // Which contact/address fields the broker actually changed (drives the sparse
  // PATCH and enables Save even when only the address or phone was edited).
  // The address is NOT in this map: it is one fact and rides whole (lib/named-insured-address.js addressForSave).
  const CONTACT_TO_BODY = { phone: 'primary_contact_phone', email: 'primary_contact_email' };
  const address = addressForSave(contact, loadedContact);
  const addressTouched = ['street', 'street2', 'city', 'st', 'zip'].some((k) => contact[k].trim() !== (loadedContact[k] || '').trim());
  const changedContactKeys = Object.keys(CONTACT_TO_BODY).filter((k) => contact[k] !== loadedContact[k]);
  const nameOrTypeChanged = trimmed !== (loadedName || '').trim() || policyholderType !== (loadedType || '');
  // Policyholder type is required to save (DISPATCH-PREFLIGHT-SPEC §1): it is
  // the field that decides whether the insured reaches a carrier as a person or
  // as an entity, and an unset one blocks the quote at the dispatch gate. The
  // server enforces the same rule, so this only saves the broker a round trip.
  const typeMissing = !policyholderType;
  const canSave = !!accountId && trimmed.length > 0 && trimmed.length <= 200 && !typeMissing && address.state !== 'partial' &&
    (nameOrTypeChanged || changedContactKeys.length > 0 || addressTouched);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSave || submitting) return;
    setSubmitting(true);
    try {
      // Sparse: only send changed contact/address fields so the server leaves
      // untouched CRM values alone (an absent key = leave alone).
      const contactPatch = {};
      for (const k of changedContactKeys) {
        const v = contact[k].trim();
        // A blank phone or email is not an answer and is never written (shared
        // inboxes, CLAUDE.md): an emptied field simply does not ride the save.
        if ((k === 'phone' || k === 'email') && !v) continue;
        contactPatch[CONTACT_TO_BODY[k]] = v;
      }
      const res = await updateNamedInsured(accountId, {
        account_name: trimmed,
        // Only send when changed from the account's live value — avoids
        // overwriting a type the broker didn't touch (and "— Not set —"
        // intentionally clears it). Baseline is the fetched current type, not
        // the stale pipeline item (which never carried policyholder_type).
        ...(policyholderType !== (loadedType || '')
          ? { policyholder_type: policyholderType }
          : {}),
        ...contactPatch,
        // The whole address, every save of a whole address (five pieces, never a subset); nothing for a half address.
        ...address.body,
        ...(cascadeDealId ? { cascade_deal_id: cascadeDealId } : {}),
      });
      // The contact save is a READ BACK of what the server did, not an echo:
      // how many terms carried the copy, how many pilot forms followed, and
      // whether the CRM mirror landed. A mirror refusal is reported beside the
      // success it does not undo (TRACON is the store that answers).
      const ci = res?.contact_info || null;
      if (ci?.applied) {
        const terms = Number(ci.sessions_updated || 0);
        const forms = Number(ci.phfs_updated || 0);
        if (Number(ci.sessions_found || 0) === 0) {
          toast('No policy term carries this contact yet; only the CRM contact was updated.');
        } else {
          toast.success(`Contact info saved on ${terms} term${terms === 1 ? '' : 's'}${forms ? ` and ${forms} pilot form${forms === 1 ? '' : 's'}` : ''}`);
        }
        if (ci.mirror && ci.mirror.applied === false) {
          toast.error(`Saved in TRACON, but the CRM contact was not updated: ${ci.mirror.error || ci.mirror.code}`);
        }
        // The person door's other warnings (pilot forms not followed, the text
        // permission of a new number, a record that did not land): one each.
        for (const w of Array.isArray(ci.warnings) ? ci.warnings : []) toast.info(w?.message || String(w));
      }
      const cascade = res?.cascade || null;
      if (cascade?.applied && cascade.link) {
        toast.success('Named insured updated · packet regenerated');
      } else {
        toast.success('Named insured updated');
      }
      if (cascade && (cascade.regen_error || (!cascade.applied && cascade.error))) {
        toast.error(`Packet not regenerated: ${cascade.regen_error || cascade.error}`);
      }
      // Tell the hangar panels (Submission, Locations & Coverages) to re-pull
      // the merged application + fresh packet link.
      if (cascade?.applied && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(HANGAR_SUBMISSION_UPDATED_EVENT, { detail: { dealId: cascadeDealId } }));
      }
      if (typeof onSaved === 'function') onSaved({ account_name: trimmed, policyholder_type: policyholderType });
      onOpenChange?.(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message || 'failed';
      toast.error(`Couldn't update: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto md:max-w-md">
        <SheetHeader>
          <SheetTitle>Edit named insured</SheetTitle>
          <SheetDescription>
            Changes the insured name on the account — it re-titles every policy term
            under this account, not just the current deal.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Named insured
            </span>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 200))}
              placeholder="e.g. Michael Byler & Kaleb Zimmerman"
              className={cn(fieldClass, 'mt-1.5')}
            />
            <span className="mt-1 block text-[10px] text-text-dim">
              {trimmed.length}/200
            </span>
          </label>

          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Policyholder type <span className="text-accent">*</span>
            </span>
            <select
              value={policyholderType}
              onChange={(e) => setPolicyholderType(e.target.value)}
              className={cn(fieldClass, 'mt-1.5', typeMissing && 'border-accent')}
            >
              {POLICYHOLDER_TYPES.map((t) => (
                <option key={t.value || 'none'} value={t.value}>{t.label}</option>
              ))}
            </select>
            {typeMissing ? (
              <span className="mt-1 block text-[10px] text-accent">
                Required. Without it the insured reaches the carrier as a nameless entity, and
                the quote is blocked at dispatch.
              </span>
            ) : null}
            <span className="mt-1 block text-[10px] text-text-dim">
              Adding a co-owner? There&rsquo;s no &ldquo;joint&rdquo; type — use Partnership and name
              both owners above, or LLC for an entity-held policy.
            </span>
          </label>

          <div className="border-t border-border pt-4">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Mailing address
            </span>
            <div className="mt-1.5 space-y-2">
              <div className="relative">
                <input type="text" value={contact.street}
                  onChange={(e) => { setContactField('street', e.target.value.slice(0, 120)); setAddrOpen(true); }}
                  onBlur={() => setTimeout(() => setAddrOpen(false), 150)}
                  placeholder="Street" className={fieldClass} />
                {addrOpen && addrSuggestions.length > 0 ? (
                  <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-card border border-border bg-bg shadow-lg">
                    {addrSuggestions.map((s, i) => (
                      <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); pickAddress(s); }}
                        className="block w-full px-3 py-1.5 text-left text-sm text-text hover:bg-border/30">
                        {s.label || `${s.street}, ${s.city}, ${s.state} ${s.zip}`}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <input type="text" value={contact.street2} onChange={(e) => setContactField('street2', e.target.value.slice(0, 120))}
                placeholder="Street 2 (optional)" className={fieldClass} />
              <div className="flex gap-2">
                <input type="text" value={contact.city} onChange={(e) => setContactField('city', e.target.value.slice(0, 80))}
                  placeholder="City" className={cn(fieldClass, 'flex-1')} />
                <input type="text" value={contact.st} onChange={(e) => setContactField('st', e.target.value.slice(0, 20))}
                  placeholder="State" className={cn(fieldClass, 'w-20')} />
                <input type="text" value={contact.zip} onChange={(e) => setContactField('zip', e.target.value.slice(0, 12))}
                  placeholder="ZIP" className={cn(fieldClass, 'w-24')} />
              </div>
              {address.state === 'partial' ? <p className="text-[11px] text-warning" data-address-partial>{PARTIAL_ADDRESS_WORDS}</p> : null}
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Primary contact
            </span>
            <p className="mt-1 text-[11px] text-text-muted">
              {primary.name
                ? <>Currently <span className="font-medium text-text">{primary.name}</span>. The binding application prints this person as the signer.</>
                : 'No primary contact on this account. The binding application prints this person as the signer, so pick or create one.'}
              {/* The person's own name, email and phone are edited on the person
                  sheet (ruling 1, C5); this sheet only picks WHO is primary. */}
              {primary.id ? (
                <button type="button" onClick={() => openPeek(openContactPeek(primary.id))} className="ml-2 text-[11px] text-accent underline-offset-2 hover:underline" data-edit-person-name>
                  Edit name
                </button>
              ) : null}
            </p>
            <div className="relative mt-1.5">
              <input
                type="text"
                value={pickerQ}
                onChange={(e) => setPickerQ(e.target.value)}
                placeholder="Search contacts to set as primary…"
                className={fieldClass}
              />
              {pickerResults !== null || pickerBusy ? (
                <div className="mt-1 rounded-card border border-border bg-bg">
                  {pickerBusy ? (
                    <p className="px-3 py-2 text-xs text-text-dim">Searching…</p>
                  ) : pickerFailed ? (
                    <p className="px-3 py-2 text-xs text-danger" data-picker-failed>Could not search contacts. Nothing here is known to be empty; try again.</p>
                  ) : pickerResults.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-text-dim">No contacts match.</p>
                  ) : (
                    pickerResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        disabled={settingPrimary}
                        onClick={() => applyPrimary({ contact_id: c.id }, c.name)}
                        className="flex w-full items-baseline justify-between gap-2 px-3 py-1.5 text-left text-sm text-text hover:bg-border/30"
                        data-picker-row
                      >
                        <span className="truncate">{c.name}{c.email ? <span className="ml-2 text-xs text-text-muted">{c.email}</span> : null}</span>
                        {/* At account_people "both" the server words each on-account row's roles and
                            whether this account is the person's main (account-people-door T27, D-22);
                            a zoho-mode row carries neither and renders as before. */}
                        {c.on_account && !Array.isArray(c.role_words) && !c.main_word ? <span className="shrink-0 text-[10px] uppercase text-accent">this account</span> : null}
                        {c.on_account && (Array.isArray(c.role_words) || c.main_word) ? (
                          <span className="flex shrink-0 flex-wrap items-baseline justify-end gap-1">
                            {(Array.isArray(c.role_words) ? c.role_words : []).map((w, i) => <span key={`${w}${i}`} className="rounded-pill border border-border px-1.5 text-[10px] text-text-muted" data-picker-role>{w}</span>)}
                            {c.main_word ? <span className="rounded-pill border border-accent px-1.5 text-[10px] text-text" data-picker-main>{c.main_word}</span> : null}
                            <span className="text-[10px] uppercase text-accent">this account</span>
                          </span>
                        ) : null}
                      </button>
                    ))
                  )}
                  <button
                    type="button"
                    onClick={() => setShowCreate((s) => !s)}
                    className="w-full border-t border-border px-3 py-1.5 text-left text-xs text-accent hover:bg-border/30"
                  >
                    Not here? Add a new contact
                  </button>
                </div>
              ) : null}
            </div>
            {showCreate ? (
              <div className="mt-2 space-y-2 rounded-card border border-border p-2.5">
                <div className="flex gap-2">
                  <input type="text" value={newContact.first_name} onChange={(e) => setNewContact((c) => ({ ...c, first_name: e.target.value.slice(0, 60) }))}
                    placeholder="First name" className={cn(fieldClass, 'flex-1')} />
                  <input type="text" value={newContact.last_name} onChange={(e) => setNewContact((c) => ({ ...c, last_name: e.target.value.slice(0, 60) }))}
                    placeholder="Last name" className={cn(fieldClass, 'flex-1')} />
                </div>
                <input type="email" value={newContact.email} onChange={(e) => setNewContact((c) => ({ ...c, email: e.target.value.slice(0, 120) }))}
                  placeholder="Email" className={fieldClass} />
                <input type="tel" value={newContact.phone} onChange={(e) => setNewContact((c) => ({ ...c, phone: e.target.value.slice(0, 40) }))}
                  placeholder="Phone" className={fieldClass} />
                <Button
                  type="button"
                  size="sm"
                  disabled={settingPrimary || !newContact.first_name.trim() || !newContact.last_name.trim()}
                  onClick={() => applyPrimary({ create: {
                    first_name: newContact.first_name.trim(),
                    last_name: newContact.last_name.trim(),
                    email: newContact.email.trim(),
                    phone: newContact.phone.trim(),
                  } }, `${newContact.first_name.trim()} ${newContact.last_name.trim()}`)}
                >
                  {settingPrimary ? 'Creating…' : 'Create under this account and set as primary'}
                </Button>
              </div>
            ) : null}
            <div className="mt-1.5 space-y-2">
              <input type="tel" value={contact.phone} onChange={(e) => setContactField('phone', e.target.value.slice(0, 40))}
                placeholder="Phone" className={fieldClass} />
              <input type="email" value={contact.email} onChange={(e) => setContactField('email', e.target.value.slice(0, 120))}
                placeholder="Email" className={fieldClass} />
            </div>
            <span className="mt-1 block text-[10px] text-text-dim">
              Saved in TRACON on every term under this account and mirrored to the CRM contact.
              A CRM refusal (for example an email another contact already uses) is reported here
              and does not undo the save. Blank fields are left as they are.
            </span>
          </div>

          <SheetFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave || submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
